import { AlertTriangle, CheckCircle2, CircleSlash, MinusCircle } from 'lucide-react';
import { Link } from 'react-router';
import { RelativeTime } from '../../components/ui/Time.js';
import { cx } from '../../lib/cx.js';
import { formatCount } from '../../lib/format.js';
import { PROVIDER_HEALTH_TOKENS, type ProviderHealth, type ProviderStatus } from './providers.js';

const ICONS: Record<ProviderHealth, typeof CheckCircle2> = {
  healthy: CheckCircle2,
  degraded: AlertTriangle,
  failing: AlertTriangle,
  disabled: MinusCircle,
  not_configured: CircleSlash,
};

/**
 * One tile per SaaS provider: current health (icon **and** label, never colour
 * alone), active incidents and the last successful poll.
 */
export function ProviderBoard({
  statuses,
  className,
}: {
  statuses: ProviderStatus[];
  className?: string;
}) {
  return (
    <ul className={cx('grid gap-2 sm:grid-cols-2 lg:grid-cols-3', className)}>
      {statuses.map((status) => {
        const token = PROVIDER_HEALTH_TOKENS[status.health];
        const Icon = ICONS[status.health];
        return (
          <li
            key={status.provider.key}
            className="rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] p-3"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-[var(--text-base)] font-medium text-[var(--text)]">
                  {status.provider.label}
                </p>
                <p className="truncate text-[var(--text-sm)] text-[var(--text-3)]">
                  {status.provider.description}
                </p>
              </div>
              <span
                className="inline-flex shrink-0 items-center gap-1 text-[var(--text-sm)] font-medium"
                style={{ color: token.ink }}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {token.label}
              </span>
            </div>

            <dl className="mt-2.5 grid grid-cols-3 gap-2 text-[var(--text-sm)]">
              <div>
                <dt className="text-[var(--text-3)]">Incidents</dt>
                <dd className="tnum font-medium text-[var(--text)]">
                  {formatCount(status.activeIncidents)}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--text-3)]">Sources</dt>
                <dd className="tnum font-medium text-[var(--text)]">
                  {status.enabledCount}/{status.integrationCount}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--text-3)]">Last poll</dt>
                <dd className="font-medium text-[var(--text)]">
                  <RelativeTime value={status.lastPollAt} />
                </dd>
              </div>
            </dl>

            {status.lastError && (
              <p
                className="mt-2 truncate text-[var(--text-sm)] text-[var(--danger)]"
                title={status.lastError}
              >
                {status.lastError}
              </p>
            )}
            {status.health === 'not_configured' && (
              <Link
                to="/integrations"
                className="mt-2 inline-block text-[var(--text-sm)] text-[var(--accent-ink)] underline underline-offset-2"
              >
                Connect this provider
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}
